<template>
  <v-dialog v-model="addPromoModule" maxWidth="700">
    <v-card>
      <v-card-title>{{ submitCTA }} Prompt: {{ path.name }} </v-card-title>
      <v-card-text>
        <div v-if="canContinue" style="margin-bottom: 16px" class="sequence-continue-conditions">
          <RfSequenceAddPathConditions
            :key="path.id"
            title="continue"
            :editInteractions="editContinueInteractions"
            :disallowedInteractions="[...skipInteractions, ...editInteractions]"
            :isLastPath="editIndex === sequence.paths.length - 1"
            :path="path"
            :interval="continueInterval"
            :webClient="sequence.sequence_type === 'web_client'"
            @setInteractions="v => (editContinueInteractions = v)"
            @setInterval="v => (continueInterval = v)"
          />
        </div>
        <div v-if="canSkip" style="margin-bottom: 16px" class="sequence-skip-conditions">
          <RfSequenceAddPathConditions
            title="skips"
            :editInteractions="skipInteractions"
            :disallowedInteractions="[...editInteractions, ...continueInteractionsBySequenceType]"
            :path="path"
            @setInteractions="v => (skipInteractions = v)"
          />
        </div>
        <div class="sequence-exit-conditions">
          <RfSequenceAddPathConditions
            title="exits"
            :editInteractions="editInteractions"
            :disallowedInteractions="[...skipInteractions, ...continueInteractionsBySequenceType]"
            :path="path"
            @setInteractions="v => (editInteractions = v)"
          />
        </div>
      </v-card-text>
      <v-card-actions class="modal-card-actions-div">
        <v-btn
          class="cancel-btn"
          variant="outlined"
          width="100px"
          size="large"
          @click="emit('hideAddPromoModule')"
        >
          Cancel
        </v-btn>
        <v-btn
          size="large"
          variant="flat"
          color="success"
          :disabled="!submitEnabled"
          width="200"
          @click="addNewPath"
        >
          {{ submitCTA }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import RfSequenceAddPathConditions from "@/components/RfSequences/RfSequenceAddPathConditions.vue";
import { isEmpty } from "lodash-es";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";

const emit = defineEmits(["hideAddPromoModule", "addPath", "update:addPromoModule"]);
const props = defineProps([
  "sequence",
  "newPathSequenceId",
  "newPathInteractions",
  "isEditing",
  "newPathContinueInteractions",
  "newPathContinueInterval",
  "newPathSkipInteractions",
  "editIndex",
]);

const addPromoModule = defineModel("addPromoModule", {
  type: Boolean,
  default: false,
});

const editInteractions = ref(props.newPathInteractions);
const editPathSequenceId = ref(props.newPathSequenceId);
const editContinueInteractions = ref(props.newPathContinueInteractions);
const continueInterval = ref(props.newPathContinueInterval);
const skipInteractions = ref(props.newPathSkipInteractions);

const submitCTA = computed(() => (props.isEditing ? "Update" : "Add"));
const loop = computed(() => !!props.sequence?.filter?.loop);

const isOrdered = computed(() =>
  [GUIDE_TYPES.ordered.value, GUIDE_TYPES.web_client.value].includes(props.sequence.sequence_type),
);

const path = computed(() =>
  editPathSequenceId.value
    ? props.sequence.paths.find(({ id }) => editPathSequenceId.value === id) || {}
    : {},
);

const canContinue = computed(() => {
  if (isOrdered.value && props.editIndex < props.sequence.paths.length - 1) return true;
  if (loop.value) return true;
  return false;
});

const canSkip = computed(() => {
  return loop.value && !isEmpty(path.value.custom_filter);
});

const continueInteractionsBySequenceType = computed(() =>
  isOrdered.value ? editContinueInteractions.value : [],
);

const submitEnabled = computed(() => {
  let enabled = !!editPathSequenceId.value;
  if (canContinue.value) {
    enabled = enabled && (editContinueInteractions.value.length || skipInteractions.value.length);
  }
  return enabled;
});

const addNewPath = () => {
  addPromoModule.value = false;
  if (path.value) {
    const newPath = {
      path_name: path.value.name,
      id: path.value.id,
      interaction_types: editInteractions.value,
      continue_interaction_types: editContinueInteractions.value,
      continue_interval_minutes: continueInterval.value,
      skip_interaction_types: skipInteractions.value,
    };
    emit("addPath", newPath);
  }
};
</script>
